import { supabase } from '../supabase';
import { aBlocchi } from '../email/pubblico';
import { sessioniDelPercorso } from '@eventi/regole.ts';
import { nomeProdottoDellaRiga, tipoBiglietto } from '@eventi/biglietti.ts';
import type { CampoCliente, ClienteInserito } from '@eventi/datiCliente.ts';
import type { AulaEvento, Percorso, ProgrammaEvento, SessioneEvento, StatoAssegnazione } from '@eventi/tipi.ts';
import { costruisciProgramma } from './programma';
import { normalizzaImpostazioni } from './impostazioni';
import type { BozzaDaSalvare } from './assegnazioni';
import type { OrdineArrivato } from './avvisiBiglietti';
import { statoPdf, type InvioBiglietto } from './biglietti';
import type { EmailAcquirente, PostaAcquirente } from './daSentire';
import type {
  AnteprimaFattura,
  BigliettoEvento,
  Etichetta,
  Evento,
  EventoInElenco,
  FatturaCreata,
  ImpostazioniEvento,
  OrdineDelSito,
  PartecipanteEvento,
  PartecipazioneContatto,
  StatoLetturaOrdini,
} from './tipi';

/**
 * Accesso ai dati degli eventi (migrazione 030).
 *
 * Chi legge e chi scrive lo decide il database: si legge come i contatti, si
 * scrive solo da admin e commerciale. La conferma passa sempre dalla funzione
 * evento_conferma, che mette l'etichetta e fa partire l'email: qui non si
 * scrive mai 'confermata' a mano.
 */

// Stesso motivo di emailService: le tabelle eventi* non sono ancora in
// database.types.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Il riepilogo di una lettura degli ordini (funzione ordini-sito, lettura.ts). */
export interface EsitoLetturaOrdini {
  prova: boolean;
  ordini_letti: number;
  ordini_pagati: number;
  biglietti_nuovi: number;
  annullati: number;
  acquirenti_collegati: number;
  acquirenti_rimandati: number;
  senza_email: number;
  errori: { ordine: number | null; messaggio: string }[];
}

/** Il server non manda piu' di mille righe per volta e non avvisa del taglio. */
const BLOCCO_LETTURA = 1000;
/** Quante modifiche mandare insieme quando si salvano molte bozze. */
const BLOCCO_SCRITTURA = 10;

/** Tutte le righe di una lettura, mille per volta. La lettura deve avere un ordine fisso. */
async function leggiAPagine<T>(
  pagina: (da: number, a: number) => PromiseLike<{ data: unknown[] | null; error: unknown }>,
): Promise<T[]> {
  const righe: T[] = [];
  for (;;) {
    const { data, error } = await pagina(righe.length, righe.length + BLOCCO_LETTURA - 1);
    if (error) throw error;
    const blocco = (data ?? []) as T[];
    righe.push(...blocco);
    if (blocco.length < BLOCCO_LETTURA) return righe;
  }
}

const COLONNE_PARTECIPANTE =
  'id, contact_id, richiesta, richiesta_at, assegnazione, assegnazione_stato, confermata_at, nota, ' +
  'contatto:email_contacts(email, first_name, full_name, company_name)';

interface RigaPartecipante {
  id: string;
  contact_id: string;
  richiesta: Percorso | null;
  richiesta_at: string | null;
  assegnazione: Percorso | null;
  assegnazione_stato: StatoAssegnazione;
  confermata_at: string | null;
  nota: string | null;
  contatto: { email: string; first_name: string | null; full_name: string | null; company_name: string | null } | null;
}

function comePartecipante(r: RigaPartecipante): PartecipanteEvento {
  const email = r.contatto?.email ?? '';
  return {
    id: r.id,
    contact_id: r.contact_id,
    richiesta: r.richiesta,
    richiesta_at: r.richiesta_at,
    assegnazione: r.assegnazione,
    assegnazione_stato: r.assegnazione_stato,
    confermata_at: r.confermata_at,
    nota: r.nota,
    email,
    nome: r.contatto?.full_name?.trim() || r.contatto?.first_name?.trim() || email || 'Contatto senza nome',
    azienda: r.contatto?.company_name ?? null,
  };
}

/** Le colonne dell'ordine del sito che servono alle carte degli ordini (scheda Biglietti e Fatture). */
const COLONNE_ORDINE_SITO =
  'id, numero, stato, pagato_il, creato_il, totale, nome, cognome, email, azienda, contact_id, ' +
  'fattura_id, fattura_avviata_at, fattura_errore, fattura:invoices!ordini_sito_fattura_id_fkey(invoice_number, status)';

const COLONNE_BIGLIETTO =
  'id, token, numero, riga_id, contact_id, registrato_at, annullato_at, ' +
  `ordine:ordini_sito(${COLONNE_ORDINE_SITO}, righe), ` +
  'partecipante:email_contacts(email, full_name, first_name), ' +
  'invii:email_messages(contact_id, status, sent_at, created_at, error)';

interface RigaOrdineSito {
  id: number;
  numero: string;
  stato: string;
  pagato_il: string | null;
  creato_il: string | null;
  totale: number | string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  azienda: string | null;
  contact_id: string | null;
  fattura_id: string | null;
  fattura_avviata_at: string | null;
  fattura_errore: string | null;
  fattura: { invoice_number: string | null; status: string } | null;
}

interface RigaBiglietto {
  id: string;
  token: string;
  numero: number;
  riga_id: number;
  contact_id: string | null;
  registrato_at: string | null;
  annullato_at: string | null;
  ordine: (RigaOrdineSito & { righe?: unknown }) | null;
  partecipante: { email: string; full_name: string | null; first_name: string | null } | null;
  /** Le email del biglietto in PDF (migrazione 041). */
  invii: InvioBiglietto[] | null;
}

/** L'ordine del sito come lo mostrano le carte degli ordini. */
function comeOrdine(o: RigaOrdineSito | null): BigliettoEvento['ordine'] {
  const acquirente = [o?.nome, o?.cognome].filter(Boolean).join(' ').trim();
  return {
    id: o?.id ?? 0,
    numero: o?.numero ?? '',
    stato: o?.stato ?? '',
    pagato_il: o?.pagato_il ?? null,
    creato_il: o?.creato_il ?? null,
    totale: o?.totale != null && Number.isFinite(Number(o.totale)) ? Number(o.totale) : null,
    acquirente: acquirente || o?.email || 'Acquirente senza nome',
    email: o?.email ?? null,
    azienda: o?.azienda ?? null,
    contact_id: o?.contact_id ?? null,
    fattura: {
      id: o?.fattura_id ?? null,
      numero: o?.fattura?.invoice_number ?? null,
      annullata: o?.fattura?.status === 'cancelled',
      avviata_at: o?.fattura_avviata_at ?? null,
      errore: o?.fattura_errore ?? null,
    },
  };
}

function comeBiglietto(r: RigaBiglietto): BigliettoEvento {
  return {
    id: r.id,
    token: r.token,
    numero: r.numero,
    tipo: tipoBiglietto(nomeProdottoDellaRiga(r.ordine?.righe, r.riga_id)),
    stato: r.annullato_at ? 'annullato' : r.registrato_at ? 'registrato' : 'da_registrare',
    registrato_at: r.registrato_at,
    ordine: comeOrdine(r.ordine),
    partecipante: r.partecipante
      ? {
          email: r.partecipante.email,
          nome: r.partecipante.full_name?.trim() || r.partecipante.first_name?.trim() || r.partecipante.email,
        }
      : null,
    pdf: r.annullato_at ? null : statoPdf(Boolean(r.registrato_at), r.contact_id, r.invii ?? []),
  };
}

/** Aule e sessioni di uno o piu' eventi, raggruppate per evento. */
async function leggiProgrammi(eventi: Pick<Evento, 'id' | 'giorni' | 'turni'>[]): Promise<Map<string, ProgrammaEvento>> {
  const ids = eventi.map((e) => e.id);
  if (ids.length === 0) return new Map();
  const [aule, sessioni] = await Promise.all([
    db.from('evento_aule').select('evento_id, codice, nome, posti').in('evento_id', ids).order('codice'),
    db
      .from('evento_sessioni')
      .select('evento_id, codice, giorno, turno, turni, inizio, fine, aula, titolo, relatori, sceglibile')
      // Un programma ha qualche decina di sessioni: molto sotto le mille righe.
      .in('evento_id', ids),
  ]);
  if (aule.error) throw aule.error;
  if (sessioni.error) throw sessioni.error;
  const dell = <T extends { evento_id: string }>(righe: T[], id: string) => righe.filter((r) => r.evento_id === id);
  return new Map(
    eventi.map((e) => [
      e.id,
      costruisciProgramma(
        e,
        dell<AulaEvento & { evento_id: string }>(aule.data ?? [], e.id),
        dell<SessioneEvento & { evento_id: string }>(sessioni.data ?? [], e.id),
      ),
    ]),
  );
}

/** Un errore della funzione della fattura, con i campi del cliente da correggere se ci sono. */
export class ErroreFattura extends Error {
  constructor(
    message: string,
    readonly errori: Partial<Record<CampoCliente, string>> | null = null,
  ) {
    super(message);
    this.name = 'ErroreFattura';
  }
}

/**
 * La funzione fattura-ordine-sito. Si manda il numero dell'ordine (e, per
 * salvare il cliente, i dati scritti a mano): righe e importi li rilegge il
 * server dal database e dal sito.
 */
async function chiamaFatturaOrdine<T>(corpo: { ordine_id: number; azione: string; cliente?: ClienteInserito }): Promise<T> {
  const { data, error } = await supabase.functions.invoke('fattura-ordine-sito', { body: corpo });
  if (error) {
    // L'errore di invoke non porta il corpo: lo si legge dalla risposta, se c'e'.
    const risposta = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new ErroreFattura(risposta?.errore ?? error.message, risposta?.errori ?? null);
  }
  return data as T;
}

export const eventiService = {
  async elenco(): Promise<EventoInElenco[]> {
    const { data, error } = await db
      .from('eventi')
      .select('*, evento_partecipanti(count)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as (Evento & { evento_partecipanti?: { count: number }[] })[]).map(
      ({ evento_partecipanti, ...evento }) => ({ ...evento, partecipanti: evento_partecipanti?.[0]?.count ?? 0 }),
    );
  },

  async crea(nome: string): Promise<string> {
    const { data, error } = await db.from('eventi').insert({ nome: nome.trim() }).select('id').single();
    if (error) throw error;
    return data.id as string;
  },

  /** L'evento con il suo programma (giorni e turni dall'evento, aule e sessioni dalle tabelle). */
  async carica(id: string): Promise<{ evento: Evento; programma: ProgrammaEvento }> {
    const { data, error } = await db.from('eventi').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Evento non trovato: forse e' stato eliminato.");
    const evento = data as Evento;
    const programmi = await leggiProgrammi([evento]);
    return { evento, programma: programmi.get(id) ?? costruisciProgramma(evento, [], []) };
  },

  /** Tutti i partecipanti, con nome ed email, in ordine di nome. */
  async partecipanti(eventoId: string): Promise<PartecipanteEvento[]> {
    const righe: RigaPartecipante[] = [];
    for (;;) {
      const { data, error } = await db
        .from('evento_partecipanti')
        .select(COLONNE_PARTECIPANTE)
        .eq('evento_id', eventoId)
        .order('id')
        .range(righe.length, righe.length + BLOCCO_LETTURA - 1);
      if (error) throw error;
      const blocco = (data ?? []) as RigaPartecipante[];
      righe.push(...blocco);
      if (blocco.length < BLOCCO_LETTURA) break;
    }
    return righe.map(comePartecipante).sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
  },

  /** Le etichette e le liste di Mailing, per i menu delle impostazioni. */
  async etichette(): Promise<Etichetta[]> {
    const { data, error } = await db.from('email_tags').select('id, name, kind').order('name');
    if (error) throw error;
    return (data ?? []) as Etichetta[];
  },

  /**
   * Salva le impostazioni. Se c'e' una lista, porta dentro come partecipanti
   * anche chi c'era gia' prima: torna quanti ne sono entrati ora.
   */
  async salvaImpostazioni(id: string, impostazioni: ImpostazioniEvento): Promise<number> {
    const pulite = normalizzaImpostazioni(impostazioni);
    const { error } = await db.from('eventi').update(pulite).eq('id', id);
    if (error) throw error;
    return pulite.lista_id ? eventiService.allinea(id) : 0;
  },

  /** Apre o chiude la pagina di scelta, senza toccare le altre impostazioni. */
  async impostaRichiesteAperte(id: string, aperte: boolean): Promise<void> {
    const { error } = await db.from('eventi').update({ scelte_aperte: aperte }).eq('id', id);
    if (error) throw error;
  },

  /** Fa diventare partecipanti tutti i contatti della lista dell'evento. */
  async allinea(id: string): Promise<number> {
    const { data, error } = await db.rpc('evento_allinea_partecipanti', { p_evento_id: id });
    if (error) throw error;
    return Number(data ?? 0);
  },

  /** Importa il programma gia' controllato (vedi leggiFileProgramma). */
  async importaProgramma(id: string, programma: ProgrammaEvento): Promise<void> {
    const { error } = await db.rpc('evento_importa_programma', { p_evento_id: id, p_programma: programma });
    if (error) throw error;
  },

  /** Posti di un'aula; null = senza limite. */
  async aggiornaPosti(eventoId: string, codiceAula: string, posti: number | null): Promise<void> {
    const { error } = await db
      .from('evento_aule')
      .update({ posti })
      .eq('evento_id', eventoId)
      .eq('codice', codiceAula);
    if (error) throw error;
  },

  /**
   * Salva le bozze di una proposta. Non tocca mai chi e' gia' confermato,
   * nemmeno se nel frattempo un collega l'ha confermato: lo dice il filtro
   * sulla riga, non solo quello che c'e' sullo schermo.
   */
  async salvaBozze(eventoId: string, bozze: BozzaDaSalvare[]): Promise<void> {
    for (const pezzo of aBlocchi(bozze, BLOCCO_SCRITTURA)) {
      const esiti = await Promise.all(
        pezzo.map((b) =>
          db
            .from('evento_partecipanti')
            .update({ assegnazione: b.assegnazione, assegnazione_stato: 'bozza' })
            .eq('id', b.id)
            .eq('evento_id', eventoId)
            .neq('assegnazione_stato', 'confermata'),
        ),
      );
      const fallito = esiti.find((e: { error: unknown }) => e.error);
      if (fallito) throw fallito.error;
    }
  },

  /**
   * Modifica a mano l'assegnazione di una persona. Se era confermata torna in
   * bozza: quando la si riconferma riceve una nuova email. Un percorso vuoto
   * toglie l'assegnazione.
   */
  async salvaAssegnazione(eventoId: string, partecipanteId: string, percorso: Percorso): Promise<void> {
    const vuoto = sessioniDelPercorso(percorso).length === 0;
    const { error } = await db
      .from('evento_partecipanti')
      .update(vuoto ? { assegnazione: null, assegnazione_stato: 'nessuna' } : { assegnazione: percorso, assegnazione_stato: 'bozza' })
      .eq('id', partecipanteId)
      .eq('evento_id', eventoId);
    if (error) throw error;
  },

  /** Conferma le bozze indicate e fa partire l'email del percorso. Torna quanti sono stati confermati. */
  async conferma(eventoId: string, partecipanti: string[]): Promise<number> {
    const { data, error } = await db.rpc('evento_conferma', { p_evento_id: eventoId, p_partecipanti: partecipanti });
    if (error) throw error;
    return Number(data ?? 0);
  },

  /** I biglietti comprati sul sito per l'evento, dall'ordine piu' recente. */
  async biglietti(eventoId: string): Promise<BigliettoEvento[]> {
    const righe: RigaBiglietto[] = [];
    for (;;) {
      const { data, error } = await db
        .from('evento_biglietti')
        .select(COLONNE_BIGLIETTO)
        .eq('evento_id', eventoId)
        .order('ordine_id', { ascending: false })
        .order('riga_id')
        .order('numero')
        .range(righe.length, righe.length + BLOCCO_LETTURA - 1);
      if (error) throw error;
      const blocco = (data ?? []) as RigaBiglietto[];
      righe.push(...blocco);
      if (blocco.length < BLOCCO_LETTURA) break;
    }
    return righe.map(comeBiglietto);
  },

  /**
   * Per ogni acquirente dell'evento: il codice della sua pagina dei biglietti
   * e le email della sequenza che parte con l'etichetta "biglietti
   * acquistati" (il link per registrare e i promemoria). Serve a vedere chi
   * non l'ha ricevuta.
   */
  async postaAcquirenti(eventoId: string): Promise<Map<string, PostaAcquirente>> {
    const [{ data: evento, error: erroreEvento }, acquirenti] = await Promise.all([
      db.from('eventi').select('etichetta_acquisto_id').eq('id', eventoId).maybeSingle(),
      leggiAPagine<{ contact_id: string; token: string }>((da, a) =>
        db.from('evento_acquirenti').select('contact_id, token').eq('evento_id', eventoId).order('id').range(da, a),
      ),
    ]);
    if (erroreEvento) throw erroreEvento;

    const posta = new Map<string, PostaAcquirente>();
    for (const a of acquirenti) posta.set(a.contact_id, { token: a.token, email: [] });

    const etichetta = (evento as { etichetta_acquisto_id: string | null } | null)?.etichetta_acquisto_id;
    if (!etichetta) return posta;

    const { data: sequenze, error: erroreSequenze } = await db
      .from('email_automations')
      .select('id')
      .eq('trigger_config->>tag_id', etichetta);
    if (erroreSequenze) throw erroreSequenze;
    const ids = ((sequenze ?? []) as { id: string }[]).map((s) => s.id);
    if (ids.length === 0) return posta;

    const email = await leggiAPagine<EmailAcquirente>((da, a) =>
      db
        .from('email_messages')
        .select(
          'id, contact_id, status, scheduled_at, sent_at, delivered_at, first_clicked_at, created_at, error, ' +
            'percorrenza:email_automation_runs!inner(automation_id)',
        )
        .in('percorrenza.automation_id', ids)
        .order('id')
        .range(da, a),
    );

    for (const e of email) {
      const suaPosta = posta.get(e.contact_id);
      if (suaPosta) suaPosta.email.push(e);
      else posta.set(e.contact_id, { token: null, email: [e] });
    }
    return posta;
  },

  /** Rimette in coda l'email col biglietto in PDF per il partecipante (pulsante "Rimanda"). */
  async rimandaBiglietto(bigliettoId: string): Promise<void> {
    const { error } = await db.rpc('evento_biglietto_rimanda', { p_biglietto_id: bigliettoId });
    if (error) throw error;
  },

  /**
   * Gli ordini del sito arrivati (chi ha pagato e' collegato) dopo un certo
   * istante, dal piu' vecchio: alimentano il pallino sulla voce Eventi.
   */
  async ordiniArrivatiDopo(dopoIso: string): Promise<OrdineArrivato[]> {
    const { data, error } = await db
      .from('ordini_sito')
      .select('id, numero, nome, cognome, email, collegato_at, biglietti:evento_biglietti(evento_id, annullato_at)')
      .gt('collegato_at', dopoIso)
      .order('collegato_at')
      .limit(200);
    if (error) throw error;
    return ((data ?? []) as {
      id: number;
      numero: string;
      nome: string | null;
      cognome: string | null;
      email: string | null;
      collegato_at: string;
      biglietti: { evento_id: string; annullato_at: string | null }[] | null;
    }[]).map((o) => {
      const validi = (o.biglietti ?? []).filter((b) => !b.annullato_at);
      return {
        id: o.id,
        numero: o.numero,
        acquirente: [o.nome, o.cognome].filter(Boolean).join(' ').trim() || o.email || 'Un cliente',
        biglietti: validi.length,
        eventoId: validi[0]?.evento_id ?? o.biglietti?.[0]?.evento_id ?? null,
        collegatoAt: o.collegato_at,
      };
    });
  },

  /** Com'e' andata l'ultima lettura degli ordini dal sito. */
  async statoLetturaOrdini(): Promise<StatoLetturaOrdini | null> {
    const { data, error } = await db
      .from('ordini_sito_stato')
      .select('avviato_at, finito_at, ordini_letti, biglietti_nuovi, errore')
      .eq('fonte', 'woocommerce')
      .maybeSingle();
    if (error) throw error;
    return (data as StatoLetturaOrdini | null) ?? null;
  },

  /**
   * Legge subito gli ordini dal sito (la funzione ordini-sito). Con `prova`
   * conta soltanto, senza salvare niente. Torna il riepilogo della funzione.
   */
  async leggiOrdiniAdesso(prova: boolean): Promise<EsitoLetturaOrdini> {
    const { data, error } = await supabase.functions.invoke(prova ? 'ordini-sito?prova=1' : 'ordini-sito', { body: {} });
    if (error) {
      // L'errore di invoke non porta il corpo: lo si legge dalla risposta, se c'e'.
      const corpo = await (error as { context?: Response }).context?.json?.().catch(() => null);
      throw new Error(corpo?.messaggio ?? corpo?.errore ?? error.message);
    }
    return (data as { esito: EsitoLetturaOrdini }).esito;
  },

  /**
   * Gli ordini del sito per la pagina Fatture (Ordini dal sito): i pagati e
   * quelli con una fattura, dal piu' recente, con cosa hanno comprato.
   */
  async ordiniDelSito(): Promise<OrdineDelSito[]> {
    const { data, error } = await db
      .from('ordini_sito')
      .select(`${COLONNE_ORDINE_SITO}, righe`)
      .or('stato.in.(processing,completed),fattura_id.not.is.null,fattura_avviata_at.not.is.null')
      // I biglietti gratis (Visitatore) non si fatturano: qui sarebbero solo rumore.
      .gt('totale', 0)
      .order('id', { ascending: false })
      .limit(300);
    if (error) throw error;
    return ((data ?? []) as unknown as (RigaOrdineSito & { righe: unknown })[]).map((r) => ({
      ordine: comeOrdine(r),
      righe: (Array.isArray(r.righe) ? r.righe : [])
        .map((x) => x as { nome?: unknown; quantita?: unknown })
        .filter((x) => Number(x.quantita) > 0)
        .map((x) => ({ nome: String(x.nome ?? 'Articolo'), quantita: Number(x.quantita) })),
    }));
  },

  /** L'anteprima della fattura di un ordine del sito: calcola e mostra, non scrive niente. */
  anteprimaFattura(ordineId: number): Promise<AnteprimaFattura> {
    return chiamaFatturaOrdine<AnteprimaFattura>({ ordine_id: ordineId, azione: 'anteprima' });
  },

  /** Salva in Clienti i dati del cliente scritti a mano e li collega all'ordine. Torna l'anteprima nuova. */
  salvaClienteFattura(ordineId: number, cliente: ClienteInserito): Promise<AnteprimaFattura> {
    return chiamaFatturaOrdine<AnteprimaFattura>({ ordine_id: ordineId, azione: 'salva_cliente', cliente });
  },

  /** Crea su Fatture in Cloud la fattura di un ordine del sito (non la invia allo SDI). */
  creaFattura(ordineId: number): Promise<FatturaCreata> {
    return chiamaFatturaOrdine<FatturaCreata>({ ordine_id: ordineId, azione: 'crea' });
  },

  /** Sblocca una fattura rimasta "in creazione", dopo aver controllato su Fatture in Cloud che non esiste (solo admin). */
  async sbloccaFatturaOrdine(ordineId: number): Promise<void> {
    const { error } = await db.rpc('ordine_sito_fattura_sblocca', { p_ordine_id: ordineId });
    if (error) throw error;
  },

  /** Gli eventi a cui partecipa un contatto, con il programma per leggere i percorsi. */
  async partecipazioniDelContatto(contactId: string): Promise<PartecipazioneContatto[]> {
    const { data, error } = await db
      .from('evento_partecipanti')
      .select(
        'id, evento_id, richiesta, richiesta_at, assegnazione, assegnazione_stato, confermata_at, evento:eventi(id, nome, giorni, turni)',
      )
      .eq('contact_id', contactId);
    if (error) throw error;
    const righe = (data ?? []) as (Omit<PartecipazioneContatto, 'evento_nome' | 'programma'> & {
      evento: Pick<Evento, 'id' | 'nome' | 'giorni' | 'turni'> | null;
    })[];
    const eventi = righe.flatMap((r) => (r.evento ? [r.evento] : []));
    const programmi = await leggiProgrammi(eventi);
    return righe.flatMap(({ evento, ...r }) =>
      evento
        ? [{ ...r, evento_nome: evento.nome, programma: programmi.get(evento.id) ?? costruisciProgramma(evento, [], []) }]
        : [],
    );
  },
};
